/**
 * Social panel (KeyO) — FRIENDS | PARTY | GUILD, in the title screen's voice.
 *
 * The design is the point: this panel wears the same clothes as SOUL MERIDIAN's
 * first screen — near-black glass, the fraying meridian line as its header rule
 * (stirred on every view change, exactly as the title stirs it), thin uppercase
 * tracking for labels, lowercase text for controls, and a strand colour per
 * relationship (friends azure, party green, guild amethyst). Refusals borrow
 * the sign-in screen's red. Nothing here is a box.
 *
 * Behaviour by tab: FRIENDS — add by name, pending in/out, whisper/remove/block,
 * the shareSystem toggle. PARTY — the pending invite, the roster with its
 * leader-only ops, one-click invites for pilots in this system. GUILD — found
 * one (name + tag + charter fee), or a ranked roster with promote/demote/kick,
 * the officer-settable message of the day, and recruiting from the room.
 *
 * Free chat text NEVER renders here, and every interpolated name goes through
 * esc() — room names (guests) are not alphabet-restricted.
 */
import { GUILD_CREATE_COST, type GuildMsg, type PartyMsg, type SocialMsg } from "@hyperlight/shared";
import type { SocialModel } from "../rpg/social";
import type { PartyModel } from "../rpg/party";
import type { GuildModel } from "../rpg/guild";
import { mountMeridianRule, type Strand } from "./meridian";
import type { MeridianLine } from "./meridianLine";

type Tab = "friends" | "party" | "guild";

/** Which strand each tab (and everything inside it) is drawn in. */
const TAB_STRAND: Record<Tab, Strand> = { friends: "friends", party: "party", guild: "guild" };

/**
 * Friend/party/guild names come from the character-name registry (alphabet-
 * restricted), but the invite-NEARBY list reads names straight out of the room
 * — and a guest session's name is only trimmed and length-capped server-side.
 * Everything interpolated here goes through this first.
 */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

export class SocialPanel {
  visible = false;
  private el: HTMLElement;
  private tabsEl: HTMLElement;
  private bodyEl: HTMLElement;
  private rule: MeridianLine;
  private tab: Tab = "friends";
  /** Name whose destructive action is armed for the confirming second click. */
  private confirm: string | null = null;

  constructor(
    private social: SocialModel,
    private handlers: {
      onOp: (m: Omit<SocialMsg, "t" | "seq">) => void;
      /** Prefill the chat input with a /w line to this name. */
      onWhisper: (name: string) => void;
      onClose: () => void;
      /** Phase 3: the live party (PARTY tab renders from it). */
      party?: PartyModel;
      onPartyOp?: (m: Omit<PartyMsg, "t" | "seq">) => void;
      /** Pilots in my system right now — the invite-nearby list. */
      nearby?: () => { id: number; name: string }[];
      /** The system the player is standing in (the rally flag's target). */
      here?: () => string;
      /** Phase 4: the live guild (GUILD tab renders from it). */
      guild?: GuildModel;
      onGuildOp?: (m: Omit<GuildMsg, "t" | "seq">) => void;
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "social-panel";
    this.el.className = "mrd-panel";
    this.el.style.zIndex = "42";
    this.el.innerHTML = `
      <div class="mrd-head">
        <span class="mrd-title">social</span>
        <span class="mrd-hint">o / esc</span>
      </div>
      <div class="mrd-rule"></div>
      <div class="sp-tabs mrd-tabs"></div>
      <div class="sp-body mrd-body"></div>`;
    document.body.appendChild(this.el);
    this.tabsEl = this.el.querySelector(".sp-tabs")!;
    this.bodyEl = this.el.querySelector(".sp-body")!;
    // The title's own motif, running as this panel's rule.
    this.rule = mountMeridianRule(this.el.querySelector(".mrd-rule")!, 0.36);

    this.tabsEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-tab]") as HTMLElement | null;
      if (!btn) return;
      this.tab = btn.dataset.tab as Tab;
      this.confirm = null;
      this.rule.stir(0.8); // "whenever the screen changes, the line moves"
      this.render();
    });

    this.bodyEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-act]") as HTMLElement | null;
      if (!btn) return;
      e.stopPropagation();
      const act = btn.dataset.act!;
      const name = btn.dataset.name ?? "";
      const player = btn.dataset.player;
      /** Destructive ops arm on the first click and fire on the second. */
      const armed = (): boolean => {
        const key = `${act}:${name}`;
        if (this.confirm === key) {
          this.confirm = null;
          return true;
        }
        this.confirm = key;
        this.render();
        return false;
      };

      if (act.startsWith("g-")) {
        const op = act.slice(2) as GuildMsg["op"];
        if (op === "create") {
          this.submitGuild();
          return;
        }
        if (op === "motd") {
          this.submitMotd();
          return;
        }
        if ((op === "kick" || op === "disband" || op === "leave") && !armed()) return;
        this.handlers.onGuildOp?.({
          op,
          ...(player !== undefined ? { player: Number(player) } : {}),
          ...(name ? { name } : {}),
        });
        this.render();
      } else if (act.startsWith("p-")) {
        // Party ops carry either a room player id (invite-nearby) or a name.
        const op = act.slice(2) as PartyMsg["op"];
        if ((op === "kick" || op === "disband" || op === "leave") && !armed()) return;
        this.handlers.onPartyOp?.({
          op,
          ...(player !== undefined ? { player: Number(player) } : {}),
          ...(name ? { name } : {}),
        });
        this.render();
      } else if (act === "add") {
        this.submitAdd();
      } else if (act === "privacy") {
        this.handlers.onOp({ op: "privacy", on: !this.social.shareSystem });
      } else if (act === "whisper") {
        // Close WITHOUT onClose: that path re-locks the pointer, and the
        // pointer-lock re-acquire blurs UI fields — it would immediately
        // kick the focus out of the chat input the prefill is about to take.
        this.closeQuiet();
        this.handlers.onWhisper(name);
      } else if (act === "remove" || act === "block") {
        if (!armed()) return;
        this.handlers.onOp({ op: act, name });
        this.render();
      } else {
        this.handlers.onOp({ op: act as SocialMsg["op"], name });
      }
    });
    // ENTER submits the field it was typed in; keystrokes stay out of gameplay
    // (the Input typing gate covers focus, stopPropagation covers hotkeys).
    this.bodyEl.addEventListener("keydown", (e) => {
      const field = e.target as HTMLElement;
      if (!(field instanceof HTMLInputElement)) return;
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        e.preventDefault();
        const role = field.dataset.role;
        if (role === "guild-name" || role === "guild-tag") this.submitGuild();
        else if (role === "guild-motd") this.submitMotd();
        else this.submitAdd();
      }
      e.stopPropagation();
    });
    this.bodyEl.addEventListener("keyup", (e) => {
      if (e.target instanceof HTMLInputElement) e.stopPropagation();
    });
  }

  open(): void {
    this.visible = true;
    this.confirm = null;
    this.el.style.display = "flex";
    this.rule.start();
    this.rule.stir(1);
    this.render();
  }

  close(): void {
    if (!this.visible) return;
    this.closeQuiet();
    this.handlers.onClose();
  }

  /** Hide without the onClose handshake (no pointer re-lock) — used when
   *  focus is deliberately moving to another text surface (chat). */
  private closeQuiet(): void {
    this.visible = false;
    this.confirm = null;
    this.el.style.display = "none";
    this.rule.stop(); // a drifting line behind a closed panel is wasted frames
  }

  toggle(): void {
    if (this.visible) this.close();
    else this.open();
  }

  private submitAdd(): void {
    const field = this.bodyEl.querySelector<HTMLInputElement>("input[data-role=add-name]");
    const name = field?.value.trim() ?? "";
    if (name.length === 0) return;
    this.handlers.onOp({ op: "request", name });
    if (field) field.value = "";
  }

  /** GUILD tab: found a guild from the name + tag fields. */
  private submitGuild(): void {
    const name = this.bodyEl.querySelector<HTMLInputElement>("input[data-role=guild-name]")?.value.trim() ?? "";
    const tag = this.bodyEl.querySelector<HTMLInputElement>("input[data-role=guild-tag]")?.value.trim() ?? "";
    if (name.length === 0 || tag.length === 0) return;
    this.handlers.onGuildOp?.({ op: "create", name, tag });
  }

  /** GUILD tab: set the message of the day (officers only, server-checked). */
  private submitMotd(): void {
    const field = this.bodyEl.querySelector<HTMLInputElement>("input[data-role=guild-motd]");
    if (!field) return;
    this.handlers.onGuildOp?.({ op: "motd", motd: field.value.trim() });
    field.blur();
  }

  // --- markup helpers (one vocabulary, three tabs) -------------------------

  private act(a: string, name: string, label: string, warn = false, extra = ""): string {
    const isArmed = this.confirm === `${a}:${name}`;
    return `<button class="mrd-act${warn ? " warn" : ""}${isArmed ? " armed" : ""}" data-act="${a}"` +
      ` data-name="${esc(name)}"${extra}>${isArmed ? "confirm?" : label}</button>`;
  }

  private row(who: string, meta: string, acts: string, dim = false): string {
    return `<div class="mrd-row${dim ? " dim" : ""}">
      <span class="mrd-who">${who}${meta ? ` <span class="mrd-meta">${meta}</span>` : ""}</span>
      <span class="mrd-acts">${acts}</span>
    </div>`;
  }

  private heading(text: string): string {
    return `<div class="mrd-heading">${text}</div>`;
  }

  private empty(text: string): string {
    return `<div class="mrd-empty">${text}</div>`;
  }

  // --- tabs ---------------------------------------------------------------

  private renderFriends(): string {
    const s = this.social;
    const add =
      `<div class="mrd-form">
        <input data-role="add-name" type="text" maxlength="24" placeholder="pilot name" autocomplete="off" spellcheck="false">
        <button class="mrd-act lead" data-act="add">add friend</button>
      </div>`;

    const incoming = s.incoming.length === 0 ? "" :
      this.heading("asking for you") +
      s.incoming.map((e) => this.row(
        esc(e.name), "wants to be friends",
        this.act("accept", e.name, "accept") + this.act("decline", e.name, "decline"),
      )).join("");

    const outgoing = s.outgoing.length === 0 ? "" :
      this.heading("sent") +
      s.outgoing.map((e) => this.row(esc(e.name), "awaiting answer", "", true)).join("");

    const friends = this.heading(`friends · ${s.friends.length}`) +
      (s.friends.length === 0
        ? this.empty("no friends yet — add a pilot by name")
        : s.friends.map((f) => this.row(
            esc(f.name),
            f.online ? `● ${esc(f.system ?? "online")}` : "offline",
            (f.online ? this.act("whisper", f.name, "whisper") : "") +
              this.act("remove", f.name, "remove", true) +
              this.act("block", f.name, "block", true),
            !f.online,
          )).join(""));

    const blocked = s.blocked.length === 0 ? "" :
      this.heading("blocked") +
      s.blocked.map((e) => this.row(esc(e.name), "", this.act("unblock", e.name, "unblock"), true)).join("");

    const privacy = this.heading("presence") +
      this.row(
        "friends can see my system", "",
        `<button class="mrd-act${s.shareSystem ? " lead" : ""}" data-act="privacy" data-name="">${
          s.shareSystem ? "shown" : "hidden"}</button>`,
      );

    return add + incoming + outgoing + friends + blocked + privacy;
  }

  private renderParty(): string {
    const p = this.handlers.party;
    if (!p) return this.empty("parties are unavailable");

    const invite = p.invite
      ? this.heading("invitation") +
        this.row(
          `<b>${esc(p.invite.from)}</b>`, "invites you to their party",
          this.act("p-accept", "", "accept") + this.act("p-decline", "", "decline"),
        )
      : "";

    const roster = p.inParty
      ? this.heading(`party · ${p.members.length}`) +
        p.members.map((m) => this.row(
          `${m.leader ? "✦ " : ""}${esc(m.name)}`,
          m.online ? (m.system ?? "online") : "offline",
          m.me
            ? this.act("p-leave", m.name, "leave", true)
            : (p.isLeader
                ? this.act("p-promote", m.name, "promote") + this.act("p-kick", m.name, "kick", true)
                : ""),
          !m.online,
        )).join("") +
        (p.isLeader ? `<div class="mrd-acts end">${this.act("p-disband", "", "disband party", true)}</div>` : "")
      : this.empty("no party — invite a pilot from this system");

    // The rally flag: "everyone come to where I am", one click, any member.
    const rally = p.inParty
      ? this.heading("rally point") +
        this.row(
          p.rally ? esc(p.rally.system) : "none set",
          p.rally ? `set by ${esc(p.rally.by)}` : "your chart marks it for everyone",
          this.act("p-rally", "", p.rally?.system === (this.handlers.here?.() ?? "") ? "strike flag" : "plant here"),
        )
      : "";

    // Invite-nearby: everyone the room says is here, minus who's already in.
    const inParty = new Set(p.members.map((m) => m.name));
    const near = (this.handlers.nearby?.() ?? []).filter((n) => !inParty.has(n.name));
    const nearby = this.heading("pilots in this system") +
      (near.length === 0
        ? this.empty("nobody else out here")
        : near.map((n) => this.row(
            esc(n.name), "",
            this.act("p-invite", "", "invite", false, ` data-player="${n.id}"`),
          )).join(""));

    return invite + roster + rally + nearby;
  }

  private renderGuild(): string {
    const g = this.handlers.guild;
    if (!g) return this.empty("guilds are unavailable");

    const invite = g.invite
      ? this.heading("invitation") +
        this.row(
          `<b>${esc(g.invite.from)}</b>`,
          `invites you to [${esc(g.invite.tag)}] ${esc(g.invite.guild)}`,
          this.act("g-accept", "", "accept") + this.act("g-decline", "", "decline"),
        )
      : "";

    if (!g.inGuild) {
      return invite +
        this.heading("found a guild") +
        `<div class="mrd-form">
          <input data-role="guild-name" type="text" maxlength="24" placeholder="guild name" autocomplete="off" spellcheck="false">
          <input data-role="guild-tag" class="short" type="text" maxlength="4" placeholder="tag" autocomplete="off" spellcheck="false">
          <button class="mrd-act lead" data-act="g-create">found</button>
        </div>
        <div class="mrd-note">A charter costs ${GUILD_CREATE_COST} credits. The tag is 2–4 letters or
          digits and flies over every member's head.</div>`;
    }

    const roster = this.heading(`[${esc(g.tag)}] ${esc(g.name)} · ${g.members.length}`) +
      g.members.map((m) => {
        const me = m.me === true;
        const rank = m.role === "leader" ? "✦ leader" : m.role === "officer" ? "officer" : "";
        let acts = "";
        if (g.isLeader && !me && m.role !== "leader") {
          acts += m.role === "member"
            ? this.act("g-promote", m.name, "promote")
            : this.act("g-demote", m.name, "demote");
        }
        if (!me && (g.isLeader || (g.role === "officer" && m.role === "member"))) {
          acts += this.act("g-kick", m.name, "kick", true);
        }
        return this.row(
          esc(m.name), [rank, m.online ? (m.system ?? "online") : "offline"].filter(Boolean).join(" · "),
          acts, !m.online,
        );
      }).join("");

    // The MOTD is the guild's voice on the login screen — officers write it.
    const motd = this.heading("message of the day") +
      (g.isOfficer
        ? `<div class="mrd-form">
            <input data-role="guild-motd" type="text" maxlength="140" placeholder="a word to the guild"
              autocomplete="off" spellcheck="false" value="${esc(g.motd)}">
            <button class="mrd-act lead" data-act="g-motd">set</button>
          </div>`
        : (g.motd ? `<div class="mrd-note">${esc(g.motd)}</div>` : this.empty("nothing posted")));

    const inGuild = new Set(g.members.map((m) => m.name));
    const near = (this.handlers.nearby?.() ?? []).filter((n) => !inGuild.has(n.name));
    const recruit = g.isOfficer
      ? this.heading("recruit from this system") +
        (near.length === 0
          ? this.empty("nobody else out here")
          : near.map((n) => this.row(
              esc(n.name), "",
              this.act("g-invite", "", "invite", false, ` data-player="${n.id}"`),
            )).join(""))
      : "";

    const footer = `<div class="mrd-acts end">${
      g.isLeader ? this.act("g-disband", "", "disband guild", true) : ""}${
      this.act("g-leave", "", "leave guild", true)}</div>`;

    return invite + roster + motd + recruit + footer;
  }

  /** Re-render from the models — cheap; called on open + when a rev moves. */
  render(): void {
    if (!this.visible) return;
    const p = this.handlers.party;
    const gu = this.handlers.guild;
    const badge = (n: string): string => ` <span class="mrd-badge">${n}</span>`;
    const tab = (id: Tab, label: string, mark: string): string =>
      `<button data-tab="${id}" class="${this.tab === id ? "on" : ""}"` +
      ` style="--tab-strand:var(--mrd-${TAB_STRAND[id]})">${label}${mark}</button>`;

    this.tabsEl.innerHTML =
      tab("friends", "friends", this.social.incoming.length > 0
        ? badge(`+${this.social.incoming.length}`)
        : (this.social.friends.length > 0 ? badge(String(this.social.friends.length)) : "")) +
      tab("party", "party", p?.invite ? badge("!") : (p?.inParty ? badge(String(p.members.length)) : "")) +
      tab("guild", "guild", gu?.invite ? badge("!") : (gu?.inGuild ? badge(gu.tag) : ""));

    // The body carries the active tab's strand, so headings and presence dots
    // in it are drawn in one colour.
    this.bodyEl.style.setProperty("--row-strand", `var(--mrd-${TAB_STRAND[this.tab]})`);
    this.bodyEl.innerHTML = this.tab === "friends"
      ? this.renderFriends()
      : this.tab === "party" ? this.renderParty() : this.renderGuild();
  }
}
